'use client';

import { useState } from 'react';
import {
  convertNumberWord,
  numberWordMeta,
  type NumberWordVariant,
} from '@/lib/browser-numberword-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserNumberWordConverter({ variant }: { variant: NumberWordVariant }) {
  const meta = numberWordMeta[variant];
  const [input, setInput] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  let output = '';
  let errorMessage: string | null = null;
  try {
    output = convertNumberWord(variant, input);
  } catch (error) {
    errorMessage = error instanceof Error ? error.message : 'Could not convert the input.';
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{meta.badge}</span>
        <h2 className="tool-live-title">{meta.title}</h2>
        <p className="tool-live-subtitle">
          The result updates live in your browser. Nothing is uploaded and no server is used.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>{meta.inputLabel}</span>
            <input
              onChange={(event) => {
                setCopyMessage(null);
                setInput(event.target.value);
              }}
              placeholder={meta.sample}
              type="text"
              value={input}
            />
          </label>
          <div className="tool-live-actions">
            {output ? (
              <button className="button button-secondary" onClick={handleCopy} type="button">
                Copy result
              </button>
            ) : null}
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          {errorMessage && input.trim() ? (
            <p className="status-message status-error">{errorMessage}</p>
          ) : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>{meta.outputLabel}</span>
            <textarea readOnly rows={4} value={output} />
          </label>
          <p className="tool-live-panel-note">Conversion runs locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
